// ===============================
// OngletEquipe.jsx (PR2 Port Royal)
// ===============================
// Page responsable : composition de l’équipe médicale
// - Backend SQLite API v2 :
//      • GET    /api/v2/medecins
//      • POST   /api/v2/medecins
//      • PUT    /api/v2/medecins/:id
//      • DELETE /api/v2/medecins/:id
// - Ajout, édition et suppression de médecins
// ===============================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { apiFetch, fetchMedecinsMaitre, normalizeMedecinMaitre, readApiError } from "./apiMedecins.js";
import { confirmDialog, notify } from "./ui/dialogService.js";
import { goContextualBack } from "./contextualBack.js";

// ===============================
// Constantes
// ===============================
const CATEGORIES_JUNIOR = [
  { value: "senior", label: "Senior", shortLabel: "Senior" },
  { value: "interne_junior_pur", label: "Interne junior pur", shortLabel: "Interne junior pur" },
  { value: "dr_junior", label: "Docteur junior", shortLabel: "Docteur junior" },
  { value: "stagiaire_associe", label: "Stagiaire associé", shortLabel: "Stagiaire associé" },
];

const SECTEURS_AFFECTATION = [
  { value: "", label: "Non renseigné" },
  { value: "obstetrique", label: "Obstétrique" },
  { value: "uro_gyneco", label: "Uro/Gynéco" },
  { value: "digestif", label: "Digestif" },
  { value: "reanimation", label: "Réanimation" },
  { value: "thoracique", label: "Thoracique" },
  { value: "orthopedie", label: "Orthopédie" },
];

function normalizeCategorieJunior(value) {
  const raw = String(value || "senior").trim().toLowerCase() || "senior";
  return CATEGORIES_JUNIOR.some((c) => c.value === raw) ? raw : "senior";
}

function categorieJuniorLabel(value) {
  const normalized = normalizeCategorieJunior(value);
  return CATEGORIES_JUNIOR.find((c) => c.value === normalized)?.shortLabel || "Senior";
}

function normalizeSecteurAffectation(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return SECTEURS_AFFECTATION.some((secteur) => secteur.value === normalized) ? normalized : "";
}

function secteurAffectationLabel(value) {
  const normalized = normalizeSecteurAffectation(value);
  return SECTEURS_AFFECTATION.find((secteur) => secteur.value === normalized)?.label || "Non renseigné";
}

function normalizeMedecin(m) {
  const safe = m || {};
  return {
    id: typeof safe.id === "string" && safe.id.trim() ? safe.id : safe.medecin_id || "",
    medecin_id: typeof safe.medecin_id === "string" && safe.medecin_id.trim() ? safe.medecin_id : safe.id || "",
    nom: typeof safe.nom === "string" ? safe.nom : "",
    prenom: typeof safe.prenom === "string" ? safe.prenom : "",
    statut:
      typeof safe.statut === "string" && safe.statut.trim()
        ? safe.statut
        : "Praticien hospitalier",
    quotite: Number.isFinite(safe.quotite)
      ? safe.quotite
      : Number(safe.quotite) || 100,
    categorie_junior: normalizeCategorieJunior(safe.categorie_junior),
    secteur_affectation: normalizeSecteurAffectation(safe.secteur_affectation),
    secteurs: Array.isArray(safe.secteurs) ? safe.secteurs : [],
  };
}

function sortMedecins(list) {
  return [...list].sort((a, b) => {
    const an = String(a.nom || "");
    const bn = String(b.nom || "");
    const cmp = an.localeCompare(bn);
    if (cmp !== 0) return cmp;
    return String(a.prenom || "").localeCompare(String(b.prenom || ""));
  });
}

// ===============================
// 💬 Modal grand format : édition/ajout d’un médecin
// ===============================
function ModalEditMedecin({ medecin, onSave, onClose, titre }) {
  const [local, setLocal] = useState(() => normalizeMedecin(medecin));

  useEffect(() => {
    setLocal(normalizeMedecin(medecin));
  }, [medecin]);

  if (!medecin) return null;

  const handleChange = (field, value) => {
    setLocal((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleSubmit = () => {
    const toSave = normalizeMedecin(local);

    // garde-fous basiques
    if (!toSave.nom.trim() || !toSave.prenom.trim()) {
      notify("⚠️ Merci de renseigner un nom et un prénom.");
      return;
    }
    if (
      !Number.isFinite(toSave.quotite) ||
      toSave.quotite < 0 ||
      toSave.quotite > 100
    ) {
      notify("⚠️ La quotité doit être un nombre entre 0 et 100.");
      return;
    }

    onSave(toSave);
  };

  const modalBig = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.4)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "24px",
    },
    panel: {
      background: "#fff",
      width: "min(1000px, 95vw)",
      maxHeight: "85vh",
      overflowY: "auto",
      borderRadius: "14px",
      padding: "28px",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
      lineHeight: 1.5,
      fontSize: "17px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
    },
    h2: { fontSize: "24px", fontWeight: 700, margin: 0 },
    closeBtn: {
      border: "none",
      background: "transparent",
      fontSize: "24px",
      cursor: "pointer",
      color: "#6b7280",
    },
    field: { marginBottom: "16px" },
    label: { display: "block", fontWeight: 600, marginBottom: "6px" },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      fontSize: "16px",
    },
    select: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      fontSize: "16px",
      background: "#fff",
    },
    footer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "20px",
    },
    btn: {
      padding: "10px 16px",
      borderRadius: "8px",
      fontWeight: 600,
      cursor: "pointer",
      border: "none",
      fontSize: "16px",
    },
    btnSave: { background: "#2563eb", color: "#fff" },
    btnCancel: { background: "#e5e7eb", color: "#111827" },
  };

  return (
    <div style={modalBig.overlay}>
      <div style={modalBig.panel}>
        <div style={modalBig.header}>
          <h2 style={modalBig.h2}>{titre}</h2>
          <button onClick={onClose} style={modalBig.closeBtn}>
            ×
          </button>
        </div>

        {/* ✅ Nom / Prénom visibles aussi en modification */}
        <div style={modalBig.field}>
          <label style={modalBig.label}>Nom :</label>
          <input
            style={modalBig.input}
            value={local.nom}
            onChange={(e) => handleChange("nom", e.target.value)}
          />
        </div>

        <div style={modalBig.field}>
          <label style={modalBig.label}>Prénom :</label>
          <input
            style={modalBig.input}
            value={local.prenom}
            onChange={(e) => handleChange("prenom", e.target.value)}
          />
        </div>

        <div style={modalBig.field}>
          <label style={modalBig.label}>Statut :</label>
          <select
            style={modalBig.select}
            value={local.statut}
            onChange={(e) => handleChange("statut", e.target.value)}
          >
            <option>Praticien hospitalier</option>
            <option>Assistant spécialiste</option>
            <option>Chef de clinique</option>
            <option>Praticien contractuel</option>
            <option>Praticien attaché</option>
            <option>Stagiaire associé</option>
            <option>Contractuel temporaire</option>
            <option>Docteur Junior</option>
            <option>DFMS</option>
            <option>FFI</option>
            <option>FFI UE</option>
            <option>IHA</option>
            <option>DES socle</option>
            <option>DES appro</option>
            <option>Cumul emploi retraite</option>
            <option>PACT</option>
            <option>PUPH</option>
          </select>
        </div>

        <div style={modalBig.field}>
          <label style={modalBig.label}>Catégorie senior/junior :</label>
          <select
            style={modalBig.select}
            value={local.categorie_junior}
            onChange={(e) => handleChange("categorie_junior", e.target.value)}
          >
            {CATEGORIES_JUNIOR.map((categorie) => (
              <option key={categorie.value} value={categorie.value}>
                {categorie.label}
              </option>
            ))}
          </select>
        </div>

        {local.categorie_junior !== "senior" && (
          <div style={modalBig.field}>
            <label style={modalBig.label}>Secteur d'affectation :</label>
            <select
              style={modalBig.select}
              value={local.secteur_affectation}
              onChange={(e) => handleChange("secteur_affectation", e.target.value)}
            >
              {SECTEURS_AFFECTATION.map((secteur) => (
                <option key={secteur.value || "none"} value={secteur.value}>
                  {secteur.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={modalBig.field}>
          <label style={modalBig.label}>Quotité :</label>
          <input
            style={modalBig.input}
            type="number"
            min="0"
            max="100"
            step="10"
            value={local.quotite}
            onChange={(e) => handleChange("quotite", Number(e.target.value))}
          />
        </div>

        <div style={modalBig.footer}>
          <button
            onClick={onClose}
            style={{ ...modalBig.btn, ...modalBig.btnCancel }}
          >
            ❌ Annuler
          </button>
          <button
            onClick={handleSubmit}
            style={{ ...modalBig.btn, ...modalBig.btnSave }}
          >
            💾 Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ===============================
// 🧩 Composant principal : tableau équipe médicale
// ===============================
export default function OngletEquipe() {
  const navigate = useNavigate();

  const [medecins, setMedecins] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null); // ✅ on édite par index
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("senior");

  const editingMedecin =
    editingIndex === null ? null : medecins[editingIndex] || null;

  const seniorsCount = medecins.filter((m) => normalizeCategorieJunior(m.categorie_junior) === "senior").length;
  const juniorsCount = medecins.length - seniorsCount;
  const displayedMedecins = medecins
    .map((m, index) => ({ medecin: m, index }))
    .filter(({ medecin }) => {
      const isSenior = normalizeCategorieJunior(medecin.categorie_junior) === "senior";
      return activeTab === "senior" ? isSenior : !isSenior;
    });

  // Chargement initial
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchMedecinsMaitre();
        setMedecins(sortMedecins(data.map(normalizeMedecin)));
      } catch (err) {
        console.error("❌ Erreur chargement médecins :", err);
        notify("Erreur lors du chargement des médecins.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // --- Sauvegarde (édition) ---
  const handleSaveEdit = async (modif) => {
    try {
      const m = normalizeMedecin(modif);

      // ✅ anti-doublon : si tu changes nom/prénom vers un couple déjà existant
      const duplicate = medecins.some((x, i) => {
        if (i === editingIndex) return false;
        return (
          String(x.nom || "").toLowerCase() === m.nom.toLowerCase() &&
          String(x.prenom || "").toLowerCase() === m.prenom.toLowerCase()
        );
      });
      if (duplicate) {
        notify("⚠️ Il existe déjà un médecin avec ce nom/prénom.");
        return;
      }

      const target = editingMedecin?.id || editingMedecin?.medecin_id || editingMedecin?.nom;
      const res = await apiFetch(`/api/v2/medecins/${encodeURIComponent(target)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(m),
      });
      if (!res.ok) throw new Error(await readApiError(res, "Erreur mise à jour médecin"));
      const saved = normalizeMedecin((await res.json())?.data || m);
      setMedecins(sortMedecins(medecins.map((x, i) => (i === editingIndex ? saved : x))));

      setEditingIndex(null);
    } catch (err) {
      console.error("❌ Erreur sauvegarde médecin :", err);
      notify(err.message || "Erreur lors de la sauvegarde du médecin.");
    }
  };

  // --- Ajout ---
  const handleAddMedecin = async (newMedecin) => {
    try {
      const m = normalizeMedecin(newMedecin);

      const exists = medecins.some(
        (x) =>
          String(x.nom || "").toLowerCase() === m.nom.toLowerCase() &&
          String(x.prenom || "").toLowerCase() === m.prenom.toLowerCase()
      );
      if (exists) {
        notify("⚠️ Ce médecin existe déjà !");
        return;
      }

      const res = await apiFetch("/api/v2/medecins", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(m),
      });
      if (!res.ok) throw new Error(await readApiError(res, "Erreur ajout médecin"));
      const saved = normalizeMedecinMaitre((await res.json())?.data || m);

      setMedecins(sortMedecins([...medecins, normalizeMedecin(saved)]));
      setAdding(false);
    } catch (err) {
      console.error("❌ Erreur ajout médecin :", err);
      notify(err.message || "Erreur lors de l'ajout du médecin.");
    }
  };

  // --- Retrait du service ---
  const handleDelete = async (nom, prenom, medecinId = "") => {
    if (!(await confirmDialog(`Retirer ${prenom} ${nom} du service ? Sa fiche, son historique et ses cochages seront conservés. Ses comptes associés seront désactivés.`))) return;
    try {
      const target = medecinId || nom;
      const res = await apiFetch(
        `/api/v2/medecins/${encodeURIComponent(target)}?prenom=${encodeURIComponent(prenom)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(await readApiError(res, "Erreur retrait médecin"));

      setMedecins(medecins.filter((m) => !(m.nom === nom && m.prenom === prenom)));
    } catch (err) {
      console.error("❌ Erreur retrait médecin :", err);
      notify(err.message || "Erreur lors du retrait du médecin.");
    }
  };

  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "24px auto",
      padding: "24px",
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      fontFamily: "Inter, sans-serif",
    },
    title: { fontSize: "28px", fontWeight: 700, marginBottom: "16px" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
      textAlign: "left",
      borderBottom: "2px solid #e5e7eb",
      padding: "10px",
      background: "#f9fafb",
      fontWeight: 600,
    },
    td: {
      borderBottom: "1px solid #f1f5f9",
      padding: "10px",
      fontSize: "16px",
    },
    tabs: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      marginBottom: "16px",
      borderBottom: "1px solid #e5e7eb",
      paddingBottom: "8px",
    },
    tab: {
      padding: "9px 14px",
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      cursor: "pointer",
      fontWeight: 700,
      background: "#fff",
      color: "#374151",
    },
    tabActive: {
      background: "#111827",
      color: "#fff",
      borderColor: "#111827",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: "999px",
      background: "#eef2ff",
      color: "#3730a3",
      fontSize: "13px",
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
    button: {
      padding: "8px 12px",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      fontWeight: 600,
      background: "#111827",
      color: "#fff",
    },
    btnSecondary: { background: "#374151", marginLeft: "12px" },
    btnDelete: { background: "#b91c1c", marginLeft: "12px" },
  };

  const initialAddMedecin = {
    nom: "",
    prenom: "",
    statut: "Praticien hospitalier",
    quotite: 100,
    categorie_junior: activeTab === "junior" ? "interne_junior_pur" : "senior",
  };

  return (
    <div style={styles.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
        <h1 style={styles.title}>👩‍⚕️👨‍⚕️ Composition de l'équipe médicale</h1>
        <LogoutButton />
      </div>

      {loading ? (
        <p>Chargement…</p>
      ) : (
        <>
          <div style={styles.tabs} role="tablist" aria-label="Catégorie médecins">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "senior"}
              style={{
                ...styles.tab,
                ...(activeTab === "senior" ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab("senior")}
            >
              Médecins seniors ({seniorsCount})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "junior"}
              style={{
                ...styles.tab,
                ...(activeTab === "junior" ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab("junior")}
            >
              Médecins juniors ({juniorsCount})
            </button>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nom</th>
                <th style={styles.th}>Prénom</th>
                <th style={styles.th}>Statut</th>
                {activeTab === "junior" && <th style={styles.th}>Secteur d'affectation</th>}
                {activeTab === "junior" && <th style={styles.th}>Catégorie</th>}
                <th style={styles.th}>Quotité (%)</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedMedecins.map(({ medecin: m, index }) => (
                <tr key={`${m.id || m.nom}-${m.prenom}-${index}`}>
                  <td style={styles.td}>{m.nom}</td>
                  <td style={styles.td}>{m.prenom}</td>
                  <td style={styles.td}>{m.statut}</td>
                  {activeTab === "junior" && (
                    <td style={styles.td}>{secteurAffectationLabel(m.secteur_affectation)}</td>
                  )}
                  {activeTab === "junior" && (
                    <td style={styles.td}>
                      <span style={styles.badge}>{categorieJuniorLabel(m.categorie_junior)}</span>
                    </td>
                  )}
                  <td style={styles.td}>{m.quotite}</td>
                  <td style={styles.td}>
                    <button
                      style={styles.button}
                      onClick={() => setEditingIndex(index)}
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.btnDelete }}
                      onClick={() => handleDelete(m.nom, m.prenom, m.id || m.medecin_id)}
                    >
                        Retirer du service
                    </button>
                  </td>
                </tr>
              ))}
              {displayedMedecins.length === 0 && (
                <tr>
                  <td style={styles.td} colSpan={activeTab === "junior" ? 7 : 5}>
                    Aucun médecin enregistré dans cet onglet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      <div style={{ marginTop: "20px" }}>
        <button style={styles.button} onClick={() => setAdding(true)}>
          ➕ Ajouter un médecin
        </button>
        <button
          style={{ ...styles.button, ...styles.btnSecondary }}
          onClick={() => goContextualBack(navigate)}
        >
          ⬅️ Retour à la page précédente
        </button>
      </div>

      {/* ✅ MODALE EDIT */}
      {editingMedecin && (
        <ModalEditMedecin
          medecin={editingMedecin}
          titre="✏️ Modifier le médecin"
          onSave={handleSaveEdit}
          onClose={() => setEditingIndex(null)}
        />
      )}

      {/* ✅ MODALE ADD */}
      {adding && (
        <ModalEditMedecin
          medecin={initialAddMedecin}
          titre="➕ Ajouter un médecin"
          onSave={handleAddMedecin}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}
